import {z} from 'zod';

import {IsoUtcDateTimeSchema, UuidV4Schema} from './common.js';
import {AutonomyRecommendationSchema} from './metrics.js';

/**
 * Contratos da porta interna de métricas e autonomia.
 *
 * As duas rotas existem por causa do plano 05: a coleta diária de Insights e a
 * avaliação de autonomia são rotinas com hora marcada, e quem tem hora marcada
 * é o agendador, não o navegador.
 *
 * A diferença entre elas está na resposta, e ela é proposital. A coleta devolve
 * `202` e trabalha depois, porque medir dez mídias leva tempo e o agendador não
 * pode ficar segurando a conexão. A avaliação devolve `201` com a recomendação
 * pronta, porque ela é uma leitura do que já está no banco e porque o plano
 * pede a recomendação na resposta.
 *
 * `asOf` continua sendo correlação nas duas: a janela é medida pelo relógio do
 * servidor. Tratar `asOf` como instante de referência deixaria o chamador
 * escolher o dia da coleta e derrotar a idempotência por dia.
 */

export const MetricsCollectRequestSchema = z.strictObject({
  accountId: UuidV4Schema,
  asOf: IsoUtcDateTimeSchema,
});
export type MetricsCollectRequest = z.infer<typeof MetricsCollectRequestSchema>;

export const MetricsCollectResponseSchema = z.strictObject({
  runId: UuidV4Schema,
  status: z.enum(['queued']),
});
export type MetricsCollectResponse = z.infer<
  typeof MetricsCollectResponseSchema
>;

export const AutonomyEvaluateRequestSchema = z.strictObject({
  accountId: UuidV4Schema,
  asOf: IsoUtcDateTimeSchema,
});
export type AutonomyEvaluateRequest = z.infer<
  typeof AutonomyEvaluateRequestSchema
>;

/**
 * Resposta enxuta de propósito.
 *
 * A avaliação inteira já é servida ao painel por `/v1/autonomy/evaluations`. O
 * agendador precisa de duas coisas: o identificador para registrar e a
 * recomendação para decidir se alerta. Devolver o objeto inteiro aqui faria a
 * mesma informação viver em dois contratos, e um deles envelheceria.
 */
export const AutonomyEvaluateResponseSchema = z.strictObject({
  evaluationId: UuidV4Schema,
  recommendation: AutonomyRecommendationSchema,
});
export type AutonomyEvaluateResponse = z.infer<
  typeof AutonomyEvaluateResponseSchema
>;
